<template>
  <main class="container-1 terrain-template-list">
    <div class="container-header">
      <Breadcrumb :links="routesBread" />
      <TabNav :links="routesNav" />
    </div>

    <section class="container-body terrain-template-list__body" aria-live="polite">
      <div class="terrain-template-list__toolbar">
        <label class="terrain-template-list__search">
          Buscar por título
          <input v-model="search" type="search" placeholder="Buscar plantilla…">
        </label>
        <label class="terrain-template-list__archived-toggle">
          <input type="checkbox" v-model="includeArchived">
          Ver eliminadas
        </label>
        <router-link class="terrain-template-list__new" :to="{ name: 'terrain-templates-new' }">
          Nueva plantilla
        </router-link>
      </div>

      <p v-if="loading" class="terrain-template-list__status">Cargando plantillas…</p>
      <p v-else-if="errorMessage" class="terrain-template-list__error" role="alert">{{ errorMessage }}</p>
      <template v-else>
        <p v-if="!filteredTemplates.length" class="terrain-template-list__empty">
          {{ search ? 'Ninguna plantilla coincide con la búsqueda.' : 'No hay plantillas creadas todavía.' }}
        </p>

        <ul v-else class="terrain-template-list__list" aria-label="Plantillas de terreno">
          <li v-for="item in pagedTemplates" :key="item.id" class="terrain-template-list__card">
            <div class="terrain-template-list__row">
              <div class="terrain-template-list__info">
                <h3>{{ item.title || 'Sin título' }}</h3>
                <p v-if="item.description">{{ item.description }}</p>
                <TerrainBadge
                  :type="item.latestPublishedVersion ? 'success' : 'secondary'"
                  :message="item.latestPublishedVersion ? `Publicada v${item.latestPublishedVersion}` : 'Sin publicar'"
                />
                <TerrainBadge v-if="item.inUse" type="warning" message="En uso por una campaña" />
                <TerrainBadge v-if="item.archived" type="danger" message="Eliminada" />
              </div>
              <div class="terrain-template-list__actions">
                <a href="#" class="terrain-template-list__link" @click.prevent="openTemplate(item)">Abrir</a>
                <button type="button" :disabled="rowBusy === item.id" @click="validate(item)">Validar</button>
                <button type="button" :disabled="rowBusy === item.id || item.archived" @click="confirmPublish(item)">Publicar</button>
                <button type="button" @click="toggleVersions(item)">
                  {{ expandedId === item.id ? 'Ocultar versiones' : 'Ver versiones' }}
                </button>
                <button
                  v-if="!item.archived"
                  type="button"
                  class="terrain-template-list__danger"
                  :disabled="rowBusy === item.id"
                  @click="confirmArchive(item)"
                >Eliminar</button>
                <button
                  v-else
                  type="button"
                  :disabled="rowBusy === item.id"
                  @click="restore(item)"
                >Restaurar</button>
              </div>
            </div>

            <p
              v-if="validationResults[item.id]"
              class="terrain-template-list__validation"
              :class="{ 'terrain-template-list__validation--valid': validationResults[item.id].valid }"
              role="status"
            >
              <template v-if="validationResults[item.id].valid">La plantilla es publicable.</template>
              <template v-else>
                <strong>No es publicable:</strong>
                <ul>
                  <li v-for="(fieldError, index) in validationResults[item.id].errors" :key="index">{{ fieldError.message }}</li>
                </ul>
              </template>
            </p>

            <div v-if="expandedId === item.id" class="terrain-template-list__versions">
              <p v-if="versionsLoading" class="terrain-template-list__status">Cargando versiones…</p>
              <p v-else-if="!versions.length" class="terrain-template-list__empty">Sin versiones publicadas.</p>
              <ul v-else>
                <li v-for="version in versions" :key="version.id">
                  Versión {{ version.versionNumber }} · {{ dateLabel(version.publishedAt) }}
                </li>
              </ul>
            </div>
          </li>
        </ul>

        <Pagination
          v-if="totalPages > 1"
          :total-pages="totalPages"
          :current-page="page"
          :page-size="pageSize"
          @pagechanged="onPageChanged"
        />
      </template>
    </section>
  </main>
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb.vue";
import TabNav from "@/components/TabNav.vue";
import TerrainBadge from "@/components/terrain/TerrainBadge.vue";
import Pagination from "@/components/Pagination.vue";
import { createTerrainApi } from "@/services/terrain/api.js";
import { terrainNavLinks } from "@/services/terrain/nav.js";

export default {
  name: "TerrainTemplateList",
  components: { Breadcrumb, TabNav, TerrainBadge, Pagination },
  data() {
    return {
      allTemplates: [],
      loading: true,
      errorMessage: "",
      search: "",
      includeArchived: false,
      page: 1,
      pageSize: 10,
      rowBusy: null,
      validationResults: {},
      expandedId: null,
      versions: [],
      versionsLoading: false,
    };
  },
  computed: {
    routesBread() { return [{ name: "Terreno" }, { name: "Plantillas" }]; },
    routesNav() { return terrainNavLinks((this.$store.getters.getPermissions || {}).terrain); },
    terrainApi() { return createTerrainApi(this.$http, this.$store.getters.backendurl); },
    // Búsqueda y paginado son client-side sobre el lote ya cargado, mismo
    // criterio que TerrainCampaigns.vue: GET /templates todavía no soporta
    // filtro por texto (tarea 15.1, diferida).
    filteredTemplates() {
      const query = this.search.trim().toLowerCase();
      if (!query) return this.allTemplates;
      return this.allTemplates.filter((item) => (item.title || "").toLowerCase().includes(query));
    },
    totalPages() { return Math.max(1, Math.ceil(this.filteredTemplates.length / this.pageSize)); },
    pagedTemplates() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredTemplates.slice(start, start + this.pageSize);
    },
  },
  watch: {
    search() { this.page = 1; },
    includeArchived() {
      this.page = 1;
      this.loadTemplates();
    },
  },
  created() { this.loadTemplates(); },
  methods: {
    async loadTemplates() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const response = await this.terrainApi.listTemplates({ page: 1, size: 200, includeArchived: this.includeArchived });
        this.allTemplates = (response.data && response.data.items) || [];
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar las plantillas. Intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) {
      this.page = page;
    },
    // openTemplate: el editor (TerrainTemplates.vue) permite revisar una
    // plantilla eliminada pero bloquea "Publicar" ahí — este aviso previo
    // evita que el usuario entre sin saber que está eliminada y se
    // sorprenda con el botón deshabilitado.
    openTemplate(item) {
      const route = { name: "terrain-templates-edit", params: { templateId: item.id } };
      if (!item.archived) { this.$router.push(route); return; }
      this.$modal.show("dialog", {
        text: `"${item.title || "Esta plantilla"}" está eliminada. Podrá revisarla, pero no publicar cambios hasta restaurarla. ¿Continuar?`,
        buttons: [
          { title: "Cancelar" },
          { title: "Continuar", handler: () => { this.$modal.hide("dialog"); this.$router.push(route); } },
        ],
      });
    },
    async validate(item) {
      this.rowBusy = item.id;
      try {
        const response = await this.terrainApi.validateTemplate(item.id);
        this.$set(this.validationResults, item.id, response.data);
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo validar la plantilla.", type: "error" });
      } finally {
        this.rowBusy = null;
      }
    },
    confirmPublish(item) {
      this.$modal.show("dialog", {
        text: `¿Publicar "${item.title || "esta plantilla"}"? Se creará una nueva versión inmutable.`,
        buttons: [
          { title: "Cancelar" },
          {
            title: "Publicar",
            handler: () => {
              this.$modal.hide("dialog");
              this.publish(item);
            },
          },
        ],
      });
    },
    async publish(item) {
      this.rowBusy = item.id;
      try {
        await this.terrainApi.publishTemplate(item.id);
        this.$store.dispatch("toast/show", { message: "Plantilla publicada correctamente.", type: "success" });
        await this.loadTemplates();
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.code;
        const message = code === "TERRAIN_VALIDATION_FAILED"
          ? "La plantilla no es publicable. Use Validar para ver los errores."
          : "No se pudo publicar la plantilla.";
        this.$store.dispatch("toast/show", { message, type: "error" });
      } finally {
        this.rowBusy = null;
      }
    },
    confirmArchive(item) {
      this.$modal.show("dialog", {
        text: `¿Eliminar "${item.title || "esta plantilla"}"? Dejará de aparecer en el listado, pero las campañas que ya la usan no se ven afectadas. Puede restaurarla luego con "Ver eliminadas".`,
        buttons: [
          { title: "Cancelar" },
          {
            title: "Eliminar",
            handler: () => {
              this.$modal.hide("dialog");
              this.archive(item);
            },
          },
        ],
      });
    },
    async archive(item) {
      this.rowBusy = item.id;
      try {
        await this.terrainApi.archiveTemplate(item.id);
        this.$store.dispatch("toast/show", { message: "Plantilla eliminada.", type: "success" });
        await this.loadTemplates();
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo eliminar la plantilla.", type: "error" });
      } finally {
        this.rowBusy = null;
      }
    },
    async restore(item) {
      this.rowBusy = item.id;
      try {
        await this.terrainApi.restoreTemplate(item.id);
        this.$store.dispatch("toast/show", { message: "Plantilla restaurada.", type: "success" });
        await this.loadTemplates();
      } catch (error) {
        this.$store.dispatch("toast/show", { message: "No se pudo restaurar la plantilla.", type: "error" });
      } finally {
        this.rowBusy = null;
      }
    },
    async toggleVersions(item) {
      if (this.expandedId === item.id) {
        this.expandedId = null;
        return;
      }
      this.expandedId = item.id;
      this.versions = [];
      this.versionsLoading = true;
      try {
        const response = await this.terrainApi.listTemplateVersions(item.id, { page: 1, size: 25 });
        this.versions = (response.data && response.data.items) || [];
      } catch (error) {
        this.versions = [];
      } finally {
        this.versionsLoading = false;
      }
    },
    dateLabel(value) {
      if (!value) return "Sin fecha";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Sin fecha" : date.toLocaleString();
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-template-list__body {
  display: grid;
  gap: var(--terrain-space-lg);
  margin: 0 auto;
  max-width: 960px;
  width: 95%;
}

.terrain-template-list__toolbar {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);
  justify-content: space-between;
}

.terrain-template-list__new {
  background: var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  text-decoration: none;
  transition: background-color var(--terrain-transition);
  white-space: nowrap;

  &:hover { background: var(--color-steel); text-decoration: none; }
}

.terrain-template-list__archived-toggle {
  align-items: center;
  color: var(--color-navy);
  display: flex;
  font-size: var(--terrain-font-caption);
  font-weight: 600;
  gap: var(--terrain-space-xs);
}

.terrain-template-list__search {
  display: grid;
  gap: var(--terrain-space-xs);
  max-width: 320px;
  color: var(--color-navy);
  font-weight: 600;
  font-size: var(--terrain-font-caption);

  input {
    border: 1px solid var(--color-line);
    border-radius: var(--terrain-radius-md);
    font: inherit;
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

    &:hover { border-color: var(--color-steel); }
    &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
  }
}

.terrain-template-list__list { display: grid; gap: var(--terrain-space-md); list-style: none; margin: 0; padding: 0; }

.terrain-template-list__card {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  display: grid;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-lg);
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
}

.terrain-template-list__row {
  align-items: center;
  display: flex;
  gap: var(--terrain-space-lg);
  justify-content: space-between;
  flex-wrap: wrap;
}

.terrain-template-list__info { display: flex; align-items: center; gap: var(--terrain-space-md); flex-wrap: wrap; }
.terrain-template-list__info h3 { margin: 0; color: var(--color-navy); }
.terrain-template-list__info p { margin: 0; color: var(--color-ghost); }

.terrain-template-list__actions { display: flex; flex-wrap: wrap; gap: var(--terrain-space-xs); }

.terrain-template-list__link,
.terrain-template-list__actions button {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  background: var(--color-white);
  color: var(--color-navy);
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: var(--terrain-space-xs) var(--terrain-space-sm);
  text-decoration: none;
  transition: background-color var(--terrain-transition), border-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); border-color: var(--color-steel); }
  &:disabled { cursor: not-allowed; opacity: .5; }
}

.terrain-template-list__danger {
  color: var(--color-danger);

  &:hover:not(:disabled) { background: var(--terrain-color-danger-light); border-color: var(--color-danger); }
}

.terrain-template-list__validation {
  margin: 0;
  padding: var(--terrain-space-sm) var(--terrain-space-md);
  border-radius: var(--terrain-radius-sm);
  background: var(--terrain-color-danger-light);
  color: var(--color-danger);
  font-size: var(--terrain-font-caption);

  ul { margin: var(--terrain-space-xs) 0 0; padding-left: var(--terrain-space-lg); }

  &--valid {
    background: var(--terrain-color-success-light);
    color: var(--color-success);
  }
}

.terrain-template-list__versions {
  border-top: 1px solid var(--color-line);
  padding-top: var(--terrain-space-sm);
  color: var(--color-graphite);
  font-size: var(--terrain-font-caption);

  ul { margin: 0; padding-left: var(--terrain-space-lg); }
}

.terrain-template-list__error {
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
  margin: 0;
  padding: var(--terrain-space-md);
}

.terrain-template-list__status,
.terrain-template-list__empty {
  color: var(--color-ghost);
  margin: 0;
}

@media (max-width: 640px) {
  .terrain-template-list__row { flex-direction: column; align-items: stretch; }
}
</style>
